import React, { useState, useEffect, useRef } from 'react';
import { LogOut, LogIn, PlusCircle, UploadCloud, FileText, CheckCircle, AlertCircle, DollarSign, CreditCard, ShoppingBag, Wallet } from 'lucide-react';
import { Modal, Button, StatCard } from '../../components/ui';


// --- Barcode Display Component ---
const BarcodeDisplay = ({ value }) => {
    const svgRef = useRef(null);

    useEffect(() => {
        const scriptId = 'jsbarcode-script';
        const renderBarcode = () => {
            if (svgRef.current && window.JsBarcode) {
                try {
                    window.JsBarcode(svgRef.current, value, {
                        format: "CODE128",
                        lineColor: "#000",
                        width: 2,
                        height: 80,
                        displayValue: true,
                        fontOptions: "bold",
                        fontSize: 16
                    });
                } catch (e) {
                    console.error("JsBarcode error:", e);
                    svgRef.current.innerHTML = `<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="red">Invalid Barcode</text>`;
                }
            }
        };

        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = "https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js";
            script.async = true;
            document.body.appendChild(script);
            script.onload = renderBarcode;
        } else {
            renderBarcode();
        }
    }, [value]);

    return <svg ref={svgRef} className="mx-auto"></svg>;
};


// --- Barcode Modal ---
export const BarcodeModal = ({ card, onClose }) => (
    <Modal isOpen={!!card} onClose={onClose} size="md">
        {card && (
            <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{card.retailer}</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">Scan the barcode below at checkout</p>
                <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
                    <BarcodeDisplay value={card.code} />
                </div>
                <p className="text-xl font-mono font-bold tracking-wider text-gray-800 dark:text-gray-200 mb-2">{card.code}</p>
                {card.pin && <p className="text-gray-600 dark:text-gray-400">PIN: <span className="font-mono font-bold">{card.pin}</span></p>}
                <div className="mt-8">
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                </div>
            </div>
        )}
    </Modal>
);

// --- Header Component ---
export const Header = ({ user, onSignOut, onSignIn }) => (
    <header className="bg-white dark:bg-slate-800 shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <div className="flex items-center">
                <img src="/images/logo_with_text_and_magnifying_glass.webp" alt="Gift Scout Logo" className="h-10 mr-3" />
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white hidden sm:block">Gift Card Manager</h1>
            </div>
            <div className="flex items-center space-x-4">
                {user ? (
                    <>
                        <span className="text-sm text-gray-600 dark:text-gray-300 hidden md:block">Welcome, {user.displayName || user.email}</span>
                        <button onClick={onSignOut} className="flex items-center text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors" title="Sign Out">
                            <LogOut className="mr-2 h-5 w-5" />
                            <span className="hidden sm:inline">Sign Out</span>
                        </button>
                    </>
                ) : (
                    <button onClick={onSignIn} className="flex items-center bg-brand-700 hover:bg-brand-800 text-white font-bold py-2 px-4 rounded-lg shadow-md transition-colors">
                        <LogIn className="mr-2 h-5 w-5" />
                        Sign In
                    </button>
                )}
            </div>
        </div>
    </header>
);

// --- Page Header Component ---
export const PageHeader = ({ title, subtitle, children }) => (
    <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
        </div>
        <div className="flex items-center space-x-3">
            {children}
        </div>
    </div>
);

// --- Summary Cards Container ---
// Uses the shared StatCard primitive; `tone` colors each icon chip.
export const SummaryCards = ({ summary }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total Value" value={`$${(summary.totalPaid + summary.totalSavings).toFixed(2)}`} icon={DollarSign} tone="blue" />
        <StatCard title="Total Paid" value={`$${summary.totalPaid.toFixed(2)}`} icon={CreditCard} tone="purple" />
        <StatCard title="Total Spent" value={`$${summary.totalSpentOnCards.toFixed(2)}`} icon={ShoppingBag} tone="orange" />
        <StatCard title="Remaining" value={`$${summary.totalRemaining.toFixed(2)}`} icon={Wallet} tone="green" />
    </div>
);

// --- Loading Spinner ---
export const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-800 dark:border-green-500"></div>
    </div>
);

// --- Search Input Component ---
export const SearchInput = ({ value, onChange, placeholder = "Search by retailer..." }) => (
    <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
        </div>
        <input
            type="text"
            className="block w-full min-w-[180px] pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg leading-5 bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm transition duration-150 ease-in-out"
            placeholder={placeholder}
            aria-label={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
        />
    </div>
);

// --- Confirmation Modal ---
export const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel", isDangerous = false }) => (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" showClose={false}>
        <div className="text-center">
            {isDangerous && (
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
                    <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
            )}
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{message}</p>
            <div className="flex justify-center space-x-4">
                <Button variant="secondary" onClick={onClose}>{cancelText}</Button>
                <Button variant={isDangerous ? 'danger' : 'primary'} onClick={() => { onConfirm(); onClose(); }}>{confirmText}</Button>
            </div>
        </div>
    </Modal>
);

// --- Firebase Config Warning ---
export const FirebaseConfigWarning = () => (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 mb-8 rounded-r-lg shadow-sm">
        <div className="flex">
            <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-yellow-400 dark:text-yellow-500" />
            </div>
            <div className="ml-3">
                <p className="text-sm text-yellow-700 dark:text-yellow-200">
                    Firebase is not configured. Please set up your Firebase project and add the configuration to <code className="bg-yellow-100 dark:bg-yellow-900 px-1 py-0.5 rounded">src/firebase.js</code>.
                </p>
            </div>
        </div>
    </div>
);

// --- Empty State Component ---
export const EmptyState = ({ onAddClick }) => (
    <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4">
            <FileText className="h-12 w-12" />
        </div>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No gift cards</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by adding a new gift card.</p>
        <div className="mt-6">
            <button
                onClick={onAddClick}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
            >
                <PlusCircle className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Add Gift Card
            </button>
        </div>
    </div>
);

// --- Import Modal ---
export const ImportModal = ({ isOpen, onClose, onImport }) => {
    // eslint-disable-next-line
    // eslint-disable-next-line
    const [csvData, setCsvData] = useState('');
    const [parsedData, setParsedData] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setCsvData('');
            setParsedData([]);
            setError('');
        }
    }, [isOpen]);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target.result;
            setCsvData(text);
            parseCSV(text);
        };
        reader.readAsText(file);
    };

    const parseCSV = (text) => {
        try {
            const lines = text.split('\n').filter(line => line.trim() !== '');
            if (lines.length < 2) {
                setError('CSV file is empty or invalid.');
                setParsedData([]);
                return;
            }

            const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
            const requiredHeaders = ['retailer', 'value', 'code'];
            const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

            if (missingHeaders.length > 0) {
                setError(`Missing required headers: ${missingHeaders.join(', ')}`);
                setParsedData([]);
                return;
            }

            const data = [];
            for (let i = 1; i < lines.length; i++) {
                const values = lines[i].split(',').map(v => v.trim());
                if (values.length !== headers.length) continue;

                const row = {};
                headers.forEach((header, index) => {
                    row[header] = values[index];
                });

                data.push({
                    retailer: row.retailer,
                    initialValue: parseFloat(row.value) || 0,
                    pricePaid: parseFloat(row.price_paid || row.pricepaid || row.paid) || parseFloat(row.value) || 0,
                    spent: parseFloat(row.spent) || 0,
                    code: row.code,
                    pin: row.pin || '',
                    source: row.source || 'Imported',
                    notes: row.notes || ''
                });
            }

            setParsedData(data);
            setError('');
        } catch (err) {
            setError('Failed to parse CSV data.');
            setParsedData([]);
        }
    };

    const handleImportClick = () => {
        onImport(parsedData);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Import Gift Cards" size="xl">
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Upload CSV File</label>
                <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-slate-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <UploadCloud className="w-10 h-10 mb-3 text-gray-400" />
                            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">CSV (MAX. 10MB)</p>
                        </div>
                        <input type="file" className="hidden" accept=".csv" onChange={handleFileUpload} />
                    </label>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    {error}
                </div>
            )}

            {parsedData.length > 0 && (
                <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Preview ({parsedData.length} cards)</h4>
                    <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4 max-h-60 overflow-y-auto">
                        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-slate-600">
                                <tr>
                                    <th className="px-4 py-2">Retailer</th>
                                    <th className="px-4 py-2">Value</th>
                                    <th className="px-4 py-2">Code</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((card, index) => (
                                    <tr key={index} className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
                                        <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{card.retailer}</td>
                                        <td className="px-4 py-2">${card.initialValue}</td>
                                        <td className="px-4 py-2 font-mono">{card.code}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="mt-6 flex justify-end">
                <Button onClick={handleImportClick} disabled={parsedData.length === 0} size="lg" icon={CheckCircle}>
                    Import {parsedData.length > 0 ? `${parsedData.length} Card(s)` : ''}
                </Button>
            </div>
        </Modal>
    );
};